"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import AuthSocialButtons from "@/components/landing/auth/auth-social-buttons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ANALYTICS_EVENTS, getEmailDomain, trackEvent } from "@/lib/analytics";
import { API_BASE_URL, postJson } from "@/lib/api";
import { getSafeInternalRedirect } from "@/lib/auth-redirect";
import { buildOauthReturnSearchParams } from "@/lib/oauth-return";
import { showToast } from "@/lib/toast";
import { getErrorMessage } from "@/lib/utils";

type LoginResponse = { accessToken: string };

const SigninPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupHref, setSignupHref] = useState("/signup");

  const getRedirectTo = () => {
    const from =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("from")
        : null;
    return getSafeInternalRedirect(from);
  };

  useEffect(() => {
    const from =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("from")
        : null;
    const safeFrom = getSafeInternalRedirect(from, "");
    setSignupHref(
      safeFrom ? `/signup?from=${encodeURIComponent(safeFrom)}` : "/signup",
    );
  }, []);

  const oauth = (provider: "google" | "github") => {
    let query = "";
    try {
      query = `?${buildOauthReturnSearchParams({
        returnTo: getRedirectTo(),
      }).toString()}`;
    } catch {}
    trackEvent(ANALYTICS_EVENTS.authOauthStarted, {
      source: "signin_page",
      provider,
    });
    window.location.href = `${API_BASE_URL}/auth/${provider}${query}`;
  };

  const handleSignin = async () => {
    if (!email.trim() || !password) return;
    setLoading(true);
    try {
      await postJson<LoginResponse>("/auth/login", {
        email: email.trim(),
        password,
      });
      trackEvent(ANALYTICS_EVENTS.authSignInCompleted, {
        source: "signin_page",
        method: "password",
        email_domain: getEmailDomain(email) ?? "unknown",
      });
      showToast.success("Signed in");
      window.location.assign(getRedirectTo());
    } catch (error) {
      const message = getErrorMessage(error, "Failed to sign in");
      trackEvent(ANALYTICS_EVENTS.authFlowFailed, {
        source: "signin_page",
        stage: "password_signin",
        message,
      });
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen lg:min-h-[calc(100vh-200px)] items-center justify-center py-[100px] lg:py-16 ">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-slate-500 mt-2">
            Sign in to your Feedvote account.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <AuthSocialButtons
            onGoogle={() => oauth("google")}
            onGithub={() => oauth("github")}
            disabled={loading}
          />

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs text-slate-500">
              <span className="bg-white px-3">or continue with email</span>
            </div>
          </div>

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleSignin();
            }}
          >
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Email</Label>
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Password</Label>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="h-11"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11"
              disabled={loading || !email.trim() || !password}
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              If your account was created with Google or GitHub, use that
              provider or sign up with the same email once to create a password.
            </p>
          </form>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Don&apos;t have an account?{" "}
          <Link
            href={signupHref}
            className="text-indigo-600 font-medium hover:underline"
          >
            Sign up
          </Link>
        </p>
      </div>
    </main>
  );
};

export default SigninPage;
